import { useWorkspaceText } from '../interface-copy';
import { forwardRef, useId } from 'react';
import { isApplePlatform } from '@/desktop-downloads/platform';
import { formatCount } from '@/i18n/format';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import libraryUploadCopy from '@/i18n/ns/library-upload';
import { WorkspaceIcon } from '../icons';
import { useIntlLocale } from '../intl-locale';
import { WORKSPACE_UPLOAD_ACCEPT } from '../upload-input';
import { AddPageIcon, FilePageIcon } from './file-page-icon';
import type { LibraryDragPreview } from './use-library-drop';

/** Up to three pages fanned out, showing the types being dragged, with the count on top. */
function DropFan({ dragging }: { dragging: LibraryDragPreview }) {
  const locale = useIntlLocale();
  const pages = Array.from({ length: Math.min(3, dragging.count) }, (_, index) => {
    const label = dragging.labels[index];
    return label ? `file.${label.toLowerCase()}` : '';
  });
  return (
    <span className="ws-library-drop-fan" data-pages={pages.length} aria-hidden="true">
      {pages.map((name, index) => (
        <FilePageIcon key={index} name={name} />
      ))}
      <span className="ws-library-drop-count">
        {new Intl.NumberFormat(locale).format(dragging.count)}
      </span>
    </span>
  );
}

/**
 * The "Add files" row: the first row of the list, and one label for a hidden multi-file input, so a click anywhere
 * on it opens the picker. While files are dragged over the page it becomes the drop target; in an empty Files it
 * is the empty state.
 */
export const LibraryAddRow = forwardRef<
  HTMLInputElement,
  {
    dragging: LibraryDragPreview | null;
    empty: boolean;
    onFiles: (files: File[]) => void;
  }
>(function LibraryAddRow({ dragging, empty, onFiles }, ref) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const copy = useNamespaceCopy(libraryUploadCopy);
  const locale = useIntlLocale();
  const line = useId();
  const apple = typeof navigator !== 'undefined' && isApplePlatform(navigator);
  return (
    <label
      className="ws-library-add"
      data-empty={empty || undefined}
      data-dragging={dragging ? true : undefined}
      data-testid="library-add-files"
    >
      <input
        ref={ref}
        className="ws-sr-only"
        type="file"
        multiple
        accept={WORKSPACE_UPLOAD_ACCEPT}
        aria-label={copy.addFiles}
        aria-describedby={line}
        aria-keyshortcuts="Meta+U Control+U"
        data-testid="library-upload-input"
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? []);
          event.currentTarget.value = '';
          if (files.length) onFiles(files);
        }}
      />
      {dragging ? (
        <span className="ws-library-add-drop">
          <DropFan dragging={dragging} />
          <strong>
            {formatCopy(copy.dropTitle, {
              files: formatCount(copy.dropFiles, dragging.count, locale),
            })}
          </strong>
          <span className="ws-library-add-line">{copy.dropLine}</span>
        </span>
      ) : (
        <>
          <AddPageIcon />
          <span className="ws-library-add-copy">
            <strong>{copy.addFiles}</strong>
            <span className="ws-library-add-line" id={line}>
              {copy.addFilesLine}
            </span>
            <span className="ws-library-add-line-short" aria-hidden="true">
              {copy.addFilesLineShort}
            </span>
          </span>
          <span className="ws-library-add-actions" aria-hidden="true">
            <kbd className="ws-library-add-key">
              {apple ? <WorkspaceIcon name="command" size={11} /> : uiText('Ctrl')} U
            </kbd>
            <span className="ws-library-add-browse">{copy.browse}</span>
          </span>
        </>
      )}
    </label>
  );
});
